import {
  ChakraProps,
  Collapse,
  Menu,
  MenuButton,
  MenuList,
  Stack,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Tooltip } from '@main/complykit/components/tooltip';
import { LinkProps } from '@tanstack/react-router';
import { MouseEvent, useEffect, useState } from 'react';
import { Focusable } from 'react-aria-components';

import { SidebarNavGroupTitle } from './sidebar-nav-group-title';
import { SidebarNavItem } from './sidebar-nav-item';

export interface SidebarMenuProps extends React.ComponentProps<'a'> {
  /** Define menu items using {@link menuItem} */
  items: readonly SidebarMenuItemData[];
  isSubmenu?: boolean;
  isGroup?: boolean;
  isSidebarCollapsed?: boolean;
  isNested?: boolean;
}

export const SidebarMenu = ({
  items,
  isSubmenu,
  isGroup,
  isNested,
  isSidebarCollapsed,
  children,
  ...rest
}: SidebarMenuProps) => {
  return (
    <Stack
      spacing={0}
      pt={0}
      pb={2}
      px={isSubmenu || isGroup ? 0 : 3}
      pl={isGroup || (isSubmenu && isSidebarCollapsed) ? 0 : 3}
    >
      {items.map((item, i) => (
        <SidebarMenuItem
          /**
           * Use index only as a last resort. Our menu has dynamic items, which results in incorrect behavior if items are added/removed.
           */
          key={item.href || (typeof item.label === 'string' ? item.label : i)}
          {...rest}
          item={item}
          isFirstItem={i === 0}
          isSubitem={isSubmenu}
          isNested={isNested}
          isSidebarCollapsed={isSidebarCollapsed}
        />
      ))}
      {children}
    </Stack>
  );
};

export interface SidebarMenuItemProps extends React.ComponentProps<'a'> {
  item: SidebarMenuItemData;
  isSubitem?: boolean;
  isFirstItem?: boolean;
  isSidebarCollapsed?: boolean;
  isNested?: boolean;
}

export type SidebarMenuItemData = {
  label: React.ReactNode;
  /** Collapsed tooltip text for items whose label is not a plain string. */
  tooltipLabel?: string;
  icon?: React.ElementType;
  shortcut?: React.ReactNode;
  children?: readonly SidebarMenuItemData[];
  chakraProps?: ChakraProps;
  isGroup?: boolean;
  /*
   * When true, expands the submenu without animation. Does not auto-collapse — the user stays in control of closing.
   */
  autoOpen?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
} & Omit<LinkProps, 'children'>;

export function menuItem<TItem extends SidebarMenuItemData>(item: TItem): TItem {
  return item;
}

export const SidebarMenuItem = ({ item, isNested, isFirstItem, ...rest }: SidebarMenuItemProps) => {
  const { icon, children, isGroup, ...restItem } = item;
  const hasChildren = !!children?.length;
  const disclosure = useDisclosure({ defaultIsOpen: item.autoOpen ?? true });
  const { onOpen } = disclosure;
  const menuDisclosure = useDisclosure();
  const [animate, setAnimate] = useState(false);

  const isOpen = hasChildren ? disclosure.isOpen : undefined;

  useEffect(() => {
    if (item.autoOpen) {
      setAnimate(false); // Disable animation for auto opening to keep it smooth and consistent with initial render
      onOpen();
    }
  }, [item.autoOpen, onOpen]);

  const bgHoverColor = useColorModeValue('blue.25', 'rgba(214, 188, 250, 0.10)');
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (hasChildren) {
      setAnimate(true);
      disclosure.onToggle();
    } else {
      restItem.onClick?.(event);
      rest.onClick?.(event);
    }
    menuDisclosure.onClose();
  };

  if (isGroup && !hasChildren) {
    return;
  }

  const submenuTooltip =
    item.tooltipLabel ?? (typeof item.label === 'string' ? item.label : undefined);
  const submenuButton = (
    <MenuButton w="full" borderRadius="md" _hover={{ bg: bgHoverColor }}>
      <SidebarNavItem
        {...rest}
        {...restItem}
        icon={icon}
        onClick={undefined}
        isSidebarCollapsed={rest.isSidebarCollapsed}
      >
        {item.label}
      </SidebarNavItem>
    </MenuButton>
  );

  return (
    <>
      {isGroup ? (
        <SidebarNavGroupTitle isSidebarCollapsed={rest.isSidebarCollapsed} isFirst={isFirstItem}>
          {item.label}
        </SidebarNavGroupTitle>
      ) : (
        (!rest.isSidebarCollapsed || (rest.isSidebarCollapsed && !hasChildren)) && (
          <SidebarNavItem
            {...rest}
            {...restItem}
            icon={icon}
            onClick={onClick}
            hasSubitems={hasChildren}
            isOpen={isOpen}
            isSidebarCollapsed={rest.isSidebarCollapsed}
            showAsTooltip={rest.isSidebarCollapsed && !isNested}
          >
            {item.label}
          </SidebarNavItem>
        )
      )}
      {hasChildren &&
        (rest.isSidebarCollapsed && !isGroup ? (
          <Menu {...menuDisclosure}>
            {/* The tooltip rides the MenuButton itself, the tab stop; the
             * inner SidebarNavItem stays bare so no second seat nests inside
             * the button. Focusable clones the trigger props on, since
             * Chakra consumes no React Aria context. */}
            {submenuTooltip ? (
              <Tooltip content={submenuTooltip} placement="right">
                <Focusable>
                  {submenuButton as React.ReactElement<React.DOMAttributes<Element>, string>}
                </Focusable>
              </Tooltip>
            ) : (
              submenuButton
            )}
            <MenuList p={2} shadow="lg" rounded="md" maxW="sm  " maxH="300px" overflowY="auto">
              <SidebarMenu
                {...rest}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                items={item.children!}
                isSubmenu
                isGroup={isGroup}
                isNested={true}
                onClick={onClick}
              />
            </MenuList>
          </Menu>
        ) : (
          <Collapse
            in={isOpen}
            animateOpacity={animate}
            transition={animate ? undefined : { enter: { duration: 0 }, exit: { duration: 0 } }}
            style={{ marginTop: '0!important' }}
          >
            <SidebarMenu
              {...rest}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              items={item.children!}
              isSubmenu={!isGroup}
              isGroup={isGroup}
              isNested={false}
              onClick={isGroup ? rest.onClick : undefined}
            />
          </Collapse>
        ))}
    </>
  );
};
